import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { Edit, Label, Launch, QrCode2, Send, Visibility } from '@mui/icons-material';
import { TabsOption } from 'global';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { CardBackground } from 'components/CardBackground/CardBackground';
import { GET_EQUIPMENT_DETAILS_BY_ID } from 'modules/equipment/api/equipment';
import { ConditionBadge, EquipmentImagesCarousel } from 'modules/equipment';
import { formatCurrency } from 'utils';
import { NO_DATA } from 'constants/common';
import { REACT_APP_OCA_BASE_URL } from 'config';
import { accountSelectors } from 'redux/accountReducer';

import {
  EquipmentDetailsTabs,
  EquipmentDetailsResponse,
  EquipmentDetailsVariables,
  EquipmentDetailsEquipment,
} from './EquipmentDetailsTypes';
import { EquipmentInfoTab } from './EquipmentInfoTab';
import { AttachmentsTab } from './AttachmentsTab';
import { MediaTab } from './MediaTab';
import { SendEquipmentFlyerModal } from 'modules/equipment/ui/components/SendEquipmentFlyerModal';

export const EquipmentDetailsPage = () => {
  const history = useHistory();
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const account = useSelector(accountSelectors.account);

  const [isSendFlyerModalOpen, setIsSendFlyerModalOpen] = useState(false);

  const { data, loading, error } = useQuery<EquipmentDetailsResponse, EquipmentDetailsVariables>(
    GET_EQUIPMENT_DETAILS_BY_ID,
    {
      variables: { id: equipmentId },
      skip: !equipmentId,
    }
  );

  const equipment: EquipmentDetailsEquipment | undefined = data?.equipment;
  const assets = equipment?.assets ?? [];
  const specs = equipment?.specs ?? null;
  const attachments = equipment?.attachments ?? [];
  const equipmentName = equipment ? [equipment.year, equipment.make, equipment.model].filter(Boolean).join(' ') : '';

  const handleViewFlyer = () => {
    const vendorGUID = account?.dynamicsAccountId;

    if (!vendorGUID || !equipmentId) return;

    const url = new URL(`${REACT_APP_OCA_BASE_URL}/psequipmentflyer`);

    url.searchParams.set('vendorGUID', vendorGUID);
    url.searchParams.append('equipmentIds', equipmentId);

    window.open(url.toString(), '_blank');
  };

  const breadcrumbConfig = [
    {
      label: 'Inventory Management',
      value: 'inventory' as const,
      onClick: () => history.push('/inventory'),
    },
    {
      label: 'Equipment Details',
      value: 'equipmentDetails' as const,
      onClick: () => {},
    },
  ];

  const tabs: TabsOption<EquipmentDetailsTabs>[] = [
    {
      label: 'Equipment Info',
      value: EquipmentDetailsTabs.EquipmentInfo,
      isVisible: true,
      component: <EquipmentInfoTab equipment={equipment} specs={specs} />,
    },
    {
      label: 'Attachments',
      value: EquipmentDetailsTabs.Attachments,
      isVisible: true,
      component: <AttachmentsTab attachments={attachments} equipment={equipment} />,
    },
    {
      label: 'Media',
      value: EquipmentDetailsTabs.Media,
      isVisible: true,
      component: <MediaTab assets={assets} />,
    },
  ];

  return (
    <PageContainer title="Equipment Details" loading={loading} error={error}>
      <Box sx={{ padding: '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <Paper elevation={1} sx={{ padding: '1.5rem', borderRadius: '0.5rem' }}>
          <Breadcrumbs currentValue="equipmentDetails" config={breadcrumbConfig} />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight={600}>
              Equipment Details
            </Typography>

            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'right', mr: 1 }}>
                Last Updated:{' '}
                {equipment?.modifiedDateTime
                  ? new Date(equipment.modifiedDateTime).toLocaleString(undefined, { hour12: false })
                  : NO_DATA}
              </Typography>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={() => history.push(`/equipment/${equipmentId}`, { from: 'equipmentDetails' })}
              >
                Edit
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={1} sx={{ padding: '1.5rem', borderRadius: '0.5rem' }}>
          {/* Equipment Title Row */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {equipmentName || NO_DATA}
              </Typography>
              <Stack direction="row" gap={2} alignItems="center" mt={0.5}>
                {equipment?.serialNumberVin && (
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <QrCode2 fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="textSecondary">
                      VIN: {equipment.serialNumberVin}
                    </Typography>
                  </Stack>
                )}
                {equipment?.stockId && (
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <Label fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="textSecondary">
                      Stock #: {equipment.stockId}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>

            <Stack direction="row" gap={1}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<Visibility />}
                onClick={handleViewFlyer}
              >
                View Flyer
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={<Send />}
                endIcon={<Launch />}
                onClick={() => setIsSendFlyerModalOpen(true)}
              >
                Send Flyer
              </Button>
            </Stack>
          </Stack>

          {/* Main Content: Image + Description + Info */}
          <Stack direction="row" gap={3} flexWrap="wrap">
            {/* Left Column: Image Carousel + badges */}
            <Box sx={{ flex: '0 0 auto', width: '280px' }}>
              <EquipmentImagesCarousel assets={assets} />

              {/* Status Badges */}
              <Stack direction="row" gap={1} mt={1.5} alignItems="center">
                {equipment?.inventoryItem?.status && (
                  <Chip
                    label={equipment.inventoryItem.status}
                    size="small"
                    variant="outlined"
                    sx={{
                      backgroundColor: '#f0faf0',
                      color: '#2e7d32',
                      borderColor: '#c8e6c9',
                      borderRadius: '1rem',
                      fontWeight: 500,
                    }}
                  />
                )}
                {(equipment?.inventoryItem?.condition || equipment?.newUsed) && (
                  <>
                    <Typography variant="body2" color="textSecondary">
                      •
                    </Typography>
                    <ConditionBadge condition={(equipment?.inventoryItem?.condition || equipment?.newUsed)!} />
                  </>
                )}
              </Stack>

              {/* Prices */}
              <Stack direction="row" gap={3} mt={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    List Price:
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {equipment?.retailPrice != null ? `${formatCurrency(equipment.retailPrice, true)} USD` : NO_DATA}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Right Column: Description + Tabs */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Description */}
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ textTransform: 'none' }}>
                  Description
                </Typography>
                <Typography variant="body1">{equipment?.additionalDescription || NO_DATA}</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'right' }}>
                  Created:{' '}
                  {equipment?.createdDateTime
                    ? new Date(equipment.createdDateTime).toLocaleString(undefined, { hour12: false })
                    : NO_DATA}
                </Typography>
              </Box>

              {/* Tabs */}
              <TabsContainer<EquipmentDetailsTabs> tabs={tabs} isSyncWithAnchor TabsLabelWrapper={CardBackground} />
            </Box>
          </Stack>
        </Paper>
      </Box>
      {equipmentId && (
        <SendEquipmentFlyerModal
          equipmentId={equipmentId}
          open={isSendFlyerModalOpen}
          onClose={() => setIsSendFlyerModalOpen(false)}
        />
      )}
    </PageContainer>
  );
};
